import React from 'react';
import { APP_HEX, APP_BG } from '@/constant/theme/colors.constant';

// ── Shared shimmer CSS (mirrors SkeletonRows.tsx token names exactly) ─────────
const DASH_SK_CSS = `
  @keyframes skWave {
    0%   { transform: translateX(-100%); }
    60%  { transform: translateX(100%); }
    100% { transform: translateX(100%); }
  }
  .sk-bar {
    position: relative; overflow: hidden;
    background-color: ${APP_HEX.skeletonBar}; border-radius: 9999px;
  }
  .sk-pill {
    position: relative; overflow: hidden;
    background-color: ${APP_HEX.skeletonPill}; border-radius: 9999px;
    height: 22px; width: 72px;
  }
  .sk-circle {
    position: relative; overflow: hidden;
    background-color: ${APP_HEX.skeletonCircle}; border-radius: 9999px;
  }
  .sk-block {
    position: relative; overflow: hidden;
    background-color: ${APP_HEX.skeletonBar}; border-radius: 12px;
  }
  .sk-bar::after, .sk-pill::after, .sk-circle::after, .sk-block::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,127,48,0.14) 50%, transparent 100%);
    animation: skWave 1.6s ease-in-out var(--sk-delay, 0s) infinite;
  }
  .dark .sk-bar    { background-color: ${APP_HEX.skeletonDarkBar}; }
  .dark .sk-pill   { background-color: ${APP_HEX.skeletonDarkPill}; }
  .dark .sk-circle { background-color: ${APP_HEX.skeletonDarkCircle}; }
  .dark .sk-block  { background-color: ${APP_HEX.skeletonDarkBar}; }
  .dark .sk-bar::after, .dark .sk-pill::after, .dark .sk-circle::after, .dark .sk-block::after {
    background: linear-gradient(90deg, transparent 0%, rgba(255,127,48,0.22) 50%, transparent 100%);
  }
  @keyframes dashChartIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/** Injects the shared shimmer CSS once at the top of the Dashboard */
export const DashSkStyle = () => <style>{DASH_SK_CSS}</style>;

// ─────────────────────────────────────────────────────────────────────────────

/** Mirrors DashboardStatCard: icon strip on left, title + value bars on right */
export const StatSkeleton = ({ delay = 0 }: { delay?: number }) => (
  <div
    className={`relative flex overflow-hidden rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm ${APP_BG.card}`}
  >
    {/* left accent strip */}
    <div className="flex items-center justify-center w-16 sm:w-[82px] shrink-0 py-4 sm:py-5 border-r border-gray-100/60 dark:border-white/[0.04] bg-gray-50/80 dark:bg-white/[0.025]">
      <div
        className="sk-circle w-10 h-10"
        style={{ '--sk-delay': `${delay}s` } as React.CSSProperties}
      />
    </div>
    {/* right: title bar + value bar */}
    <div className="flex flex-col justify-center px-4 py-4 gap-3 flex-1">
      <div
        className="sk-bar h-2.5"
        style={
          {
            width: '58%',
            '--sk-delay': `${delay + 0.08}s`,
          } as React.CSSProperties
        }
      />
      <div
        className="sk-bar h-7"
        style={
          {
            width: '38%',
            '--sk-delay': `${delay + 0.16}s`,
          } as React.CSSProperties
        }
      />
    </div>
  </div>
);

/** Mimics a bar chart: header bars + animated column bars */
const CHART_BAR_HEIGHTS_20 = [58, 80, 44, 92, 68, 54, 84, 62, 74, 50, 88, 40, 70, 56, 78, 46, 90, 64, 52, 82];
const CHART_BAR_HEIGHTS_12 = [58, 80, 44, 92, 68, 54, 84, 62, 74, 50, 88, 40];

export const ChartSkeleton = ({
  height = 300,
  delay = 0,
  barCount = 12,
}: {
  height?: number;
  delay?: number;
  barCount?: number;
}) => (
  <div
    className="rounded-2xl border border-gray-100 dark:border-white/[0.06] overflow-hidden shadow-sm"
    style={{ height }}
  >
    {/* ── Header band – mirrors SectionCard non-compact header ── */}
    <div className={`flex items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-white/[0.06] ${APP_BG.canvas}`}>
      <div className="flex items-center gap-3">
        <div
          className="sk-circle w-9 h-9 shrink-0"
          style={{ '--sk-delay': `${delay}s` } as React.CSSProperties}
        />
        <div className="flex flex-col gap-1.5">
          <div
            className="sk-bar h-3"
            style={{ width: '120px', '--sk-delay': `${delay + 0.04}s` } as React.CSSProperties}
          />
          <div
            className="sk-bar h-2"
            style={{ width: '80px', '--sk-delay': `${delay + 0.08}s` } as React.CSSProperties}
          />
        </div>
      </div>
      {/* chart-type switcher: 3 icon button blocks */}
      <div className="flex gap-1 shrink-0">
        {[0, 1, 2].map((j) => (
          <div
            key={j}
            className="sk-block w-6 h-6"
            style={{ borderRadius: '6px', '--sk-delay': `${delay + 0.1 + j * 0.04}s` } as React.CSSProperties}
          />
        ))}
      </div>
    </div>
    {/* ── Chart columns — padding mirrors GtChart margins (left≈48 right=14 top=20 bot=44) ── */}
    <div
      className={`flex items-end ${APP_BG.base}`}
      style={{
        height: 'calc(100% - 65px)',
        paddingLeft: 48,
        paddingRight: 14,
        paddingTop: 20,
        paddingBottom: 24,
        gap: 6,
      }}
    >
      {(barCount > 12 ? CHART_BAR_HEIGHTS_20 : CHART_BAR_HEIGHTS_12).map((h, i) => (
        <div
          key={i}
          className="sk-block flex-1"
          style={{
            height: `${h}%`,
            borderRadius: '4px 4px 0 0',
            '--sk-delay': `${delay + i * 0.06}s`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  </div>
);

/**
 * Full Recent Alerts card skeleton — mirrors the SectionCard wrapper with its
 * header band (bell icon + title + subtitle + "View all" link) plus body rows.
 * Use this instead of bare AlertRowsSkeleton when the header should also shimmer.
 */
export const AlertSectionSkeleton = ({
  rows = 5,
  delay = 0,
}: {
  rows?: number;
  delay?: number;
}) => (
  <div className="rounded-2xl border border-gray-100 dark:border-white/[0.06] overflow-hidden shadow-sm">
    {/* ── Header band ── */}
    <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-white/[0.06] ${APP_BG.canvas}`}>
      <div className="flex items-center gap-3">
        <div
          className="sk-circle w-9 h-9 shrink-0"
          style={{ '--sk-delay': `${delay}s` } as React.CSSProperties}
        />
        <div className="flex flex-col gap-1.5">
          <div
            className="sk-bar h-3"
            style={{ width: '110px', '--sk-delay': `${delay + 0.04}s` } as React.CSSProperties}
          />
          <div
            className="sk-bar h-2"
            style={{ width: '72px', '--sk-delay': `${delay + 0.08}s` } as React.CSSProperties}
          />
        </div>
      </div>
      {/* "View all" link shimmer */}
      <div
        className="sk-bar h-2.5"
        style={{ width: '44px', '--sk-delay': `${delay + 0.1}s` } as React.CSSProperties}
      />
    </div>
    {/* ── Body rows ── */}
    <div className={APP_BG.base}>
      <AlertRowsSkeleton rows={rows} delay={delay + 0.12} />
    </div>
  </div>
);

/** Mimics alert rows: circle + two text bars + two status pills */
export const AlertRowsSkeleton = ({
  rows = 5,
  delay = 0,
}: {
  rows?: number;
  delay?: number;
}) => (
  <div className="flex flex-col divide-y divide-gray-50 dark:divide-white/[0.035]">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="grid grid-cols-[1fr_auto_auto] gap-x-2.5 items-center px-4 py-2.5"
      >
        <div className="flex items-center gap-2">
          <div
            className="sk-circle w-[28px] h-[28px] shrink-0"
            style={
              { '--sk-delay': `${delay + i * 0.06}s` } as React.CSSProperties
            }
          />
          <div className="flex flex-col gap-1.5">
            <div
              className="sk-bar h-2"
              style={
                {
                  width: '110px',
                  '--sk-delay': `${delay + i * 0.06 + 0.04}s`,
                } as React.CSSProperties
              }
            />
            <div
              className="sk-bar h-1.5"
              style={
                {
                  width: '72px',
                  '--sk-delay': `${delay + i * 0.06 + 0.08}s`,
                } as React.CSSProperties
              }
            />
          </div>
        </div>
        <div
          className="sk-pill"
          style={
            {
              '--sk-delay': `${delay + i * 0.06 + 0.1}s`,
            } as React.CSSProperties
          }
        />
        <div
          className="sk-pill"
          style={
            {
              '--sk-delay': `${delay + i * 0.06 + 0.14}s`,
            } as React.CSSProperties
          }
        />
      </div>
    ))}
  </div>
);
